import React, { memo } from 'react';
import type { LayoutChangeEvent, TextProps, TextStyle, ViewProps, ViewStyle } from 'react-native';
import { IIconProps, IPressableProps, HStack } from 'native-base';

import { View, } from "../native-base";
import { Icon, } from "../Icon";
import { Text, } from "../Typography";
import { Pressable, } from "../Pressable";
import { tabItemContentContainerStyles, tabItemIconStyles, tabItemProps } from './utils';
import { useThemeColors } from '../hooks';
import { TabMode, WizardMode } from './TabModes';

const DEFAULT_ICON_SIZE = 24;

export type TabItemProps = Omit<IPressableProps, 'children'> &
    {
        /**
         * name of the tab. This should be unique like a route name
         * */
        name: string;
        /**
         * Allows to define if TabItem is active.
         * */
        active?: boolean;
        /**
         * variant according to m3 guidelines
         * */
        variant?: 'primary' | 'secondary';


        mode?: "wizard" | "tabs";
        /**
         * active color for the label and the icon. This should normally come from the parent component - (Tabs)
         * */
        activeColor?: string;
        /**
         * label of the tab
         * */
        label?: string;
        labelStyle?: TextStyle;
        labelProps?: Omit<TextProps, 'children' | 'style'>;
        /**
         * icon properties
         * */
        iconName?: string;
        iconType?: string;
        iconProps?: IIconProps;
        iconStyle?: IIconProps['style'];
        contentsContainerStyle?: ViewStyle;
        contentsContainerProps?: Omit<ViewProps, 'children' | 'style' | 'onLayout'>;
        onLayoutContent?: (e: LayoutChangeEvent) => void;

        isInvalid?: boolean;
        disabled?: boolean;
        index: number;

        render?: (value: string) => void;
    };

const emptyObj = {};

const TabItemWithActionState = 
    ({
        active = false,
        variant = 'primary',
        label,
        style,
        onLayout,
        onLayoutContent,
        iconName,
        iconType,
        iconProps,
        iconStyle: iconStyleProp = emptyObj,
        activeColor,
        labelStyle: labelStyleProp,
        labelProps,
        contentsContainerStyle: contentsContainerStyleProp,
        contentsContainerProps,
        render: _render,
        isInvalid,
        disabled,
        mode = "tabs",
        index,
        ...rest
    }: TabItemProps) => {

        const { errorColor } = useThemeColors();

        const contentContainerStyles = React.useMemo(() => [
             tabItemContentContainerStyles,
             contentsContainerStyleProp
        ], [contentsContainerStyleProp]);

        const iconStyles = React.useMemo(() => [
            tabItemIconStyles,
            !active ? {} : { color: activeColor, },
        ], [active, activeColor]);

        const labelStyles = React.useMemo(() => [
            labelStyleProp,
            !isInvalid ? {} : { color: errorColor, },
        ], [isInvalid, errorColor]);

        const renderIcon = () => {
            if (!iconName) return null;
            return (
                <Icon
                    type={iconName}
                    iconFamily={iconType}
                    size={DEFAULT_ICON_SIZE}
                    {...iconProps}
                    style={iconStyles}
                />
            )
        }

        const ModeRenderer = {
            wizard: WizardMode,
            tabs: TabMode,
        }[mode] ?? TabMode;

       
        return (
            <Pressable
                accessibilityRole="tab"
                {...tabItemProps}
                {...rest}
                onLayout={onLayout}
                disabled={disabled}>
                <>
                    <View
                        {...contentsContainerProps}
                        style={contentContainerStyles}
                        onLayout={onLayoutContent}
                        opacity={disabled ? 0.5 : undefined}>
                        
                        {renderIcon()}
                            <ModeRenderer index={index} disabled={disabled} active={active} isInvalid={isInvalid}>
                                <Text numberOfLines={1} {...labelProps} style={labelStyles} alignItems="center" justifyContent={'center'}>
                                    {label}
                                </Text>
                            </ModeRenderer>
                    </View>
                </>
            </Pressable>
        );
    };

const TabItem = memo(TabItemWithActionState);

TabItem.displayName = 'Tabs_Item';

export default TabItem;
